import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Loader2,
  FileImage,
  Upload,
  FileText,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  Search,
  Filter,
  X,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// File type icons and colors
const getFileIcon = (fileType) => {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType?.toLowerCase())) {
    return { icon: FileImage, color: 'text-blue-500', bg: 'bg-blue-50' };
  }
  return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('ca-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const AdminMedia = () => {
  const { isAdmin, authFetch } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');

  const categories = ['general', 'galeria', 'headers', 'logos', 'pdfs', 'altres'];

  useEffect(() => {
    fetchMediaFiles();
  }, []);

  const fetchMediaFiles = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/media`, {  });
      if (response.ok) {
        const data = await response.json();
        setMediaFiles(data.files || []);
      }
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Error carregant fitxers');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadTitle) {
        setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Selecciona un fitxer');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle || uploadFile.name);
      formData.append('description', uploadDescription);
      formData.append('category', uploadCategory);

      const response = await authFetch(`${API_URL}/api/admin/media/upload`, {
        method: 'POST',
        
        body: formData
      });

      if (response.ok) {
        toast.success('Fitxer pujat correctament');
        
        // Reset form
        setUploadFile(null);
        setUploadTitle('');
        setUploadDescription('');
        setUploadCategory('general');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Refresh list
        fetchMediaFiles();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error pujant fitxer');
      }
    } catch (error) {
      toast.error('Error pujant fitxer');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Segur que vols eliminar aquest fitxer?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/media/${fileId}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Fitxer eliminat');
        fetchMediaFiles();
      } else {
        toast.error('Error eliminant fitxer');
      }
    } catch (error) {
      toast.error('Error eliminant fitxer');
    }
  };

  const getFileUrl = (file) => {
    return `${API_URL}/api/media/file/${file.id}`;
  };

  const copyToClipboard = async (file) => {
    const url = getFileUrl(file);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(file.id);
      toast.success('URL copiada al porta-retalls');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Error copiant URL');
    }
  };

  // Filter files
  const filteredFiles = mediaFiles.filter(file => {
    const matchesSearch = file.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         file.original_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || 
                       (filterType === 'pdf' && file.file_type === 'pdf') ||
                       (filterType === 'image' && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(file.file_type?.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || file.category === filterCategory;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const isImage = (fileType) => ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType?.toLowerCase());

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-media">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <FileImage className="w-6 h-6" />
              Imatges i PDF
            </h1>
            <p className="text-[#666666] mt-1">
              Puja i gestiona imatges i PDFs per utilitzar al contingut
            </p>
          </div>
          <div className="text-sm text-[#888888]">
            {mediaFiles.length} fitxers
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
          <h3 className="font-semibold text-[#333333] flex items-center gap-2 mb-4">
            <Upload className="w-5 h-5" />
            Pujar Fitxer
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Fitxer (PDF, PNG, JPG, etc.)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.svg"
                onChange={handleFileSelect}
                className="cursor-pointer"
                data-testid="media-file-input"
              />
              {uploadFile && (
                <p className="text-xs text-[#888888]">
                  {uploadFile.name} ({formatFileSize(uploadFile.size)})
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Títol</Label>
              <Input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Nom del fitxer..."
                data-testid="media-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Categoria</Label>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="w-full h-10 px-3 border border-input rounded-md text-sm"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="w-full bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
                data-testid="media-upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Pujar
              </Button>
            </div>
          </div>
          
          <div className="mt-3">
            <Label>Descripció (opcional)</Label>
            <Input
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="Descripció del fitxer..."
              className="mt-1"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cercar fitxers..."
              className="pl-9"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#888888]" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-10 px-3 border border-input rounded-md text-sm"
            >
              <option value="all">Tots els tipus</option>
              <option value="pdf">PDFs</option>
              <option value="image">Imatges</option>
            </select>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="h-10 px-3 border border-input rounded-md text-sm"
            >
              <option value="all">Totes les categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Files Grid */}
        {filteredFiles.length === 0 ? (
          <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-12 text-center">
            <FileImage className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
            <p className="text-[#666666]">
              {searchTerm || filterType !== 'all' || filterCategory !== 'all' 
                ? 'No s\'han trobat fitxers amb aquests filtres'
                : 'No hi ha fitxers. Puja el primer!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredFiles.map((file) => {
              const { icon: FileIcon, color, bg } = getFileIcon(file.file_type);
              const fileUrl = getFileUrl(file);
              
              return (
                <div 
                  key={file.id} 
                  className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`media-item-${file.id}`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-[#F9F9F7] relative">
                    {isImage(file.file_type) ? (
                      <img
                        src={fileUrl}
                        alt={file.title}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setPreviewDoc(file)}
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${bg}`}>
                        <FileIcon className={`w-16 h-16 ${color}`} />
                      </div>
                    )}
                    
                    {/* Quick actions overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {isImage(file.file_type) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setPreviewDoc(file)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => copyToClipboard(file)}
                      >
                        {copiedId === file.id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="secondary" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-3">
                    <p className="font-medium text-sm text-[#333333] truncate" title={file.title}>
                      {file.title}
                    </p>
                    <p className="text-xs text-[#888888] mt-1">
                      {file.file_type?.toUpperCase()} • {formatFileSize(file.file_size)}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-[#aaa] bg-[#f5f5f5] px-2 py-0.5 rounded">
                        {file.category}
                      </span>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(file.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview Modal */}
        {previewDoc && (
          <div 
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setPreviewDoc(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-lg">
              <button
                onClick={() => setPreviewDoc(null)}
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10"
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={getFileUrl(previewDoc)}
                alt={previewDoc.title}
                className="max-w-full max-h-[85vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="p-4 bg-white border-t">
                <p className="font-medium">{previewDoc.title}</p>
                <p className="text-sm text-[#666666]">{previewDoc.original_name}</p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(previewDoc)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar URL
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usage Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">Com utilitzar els fitxers:</h4>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
            <li>Puja el fitxer (imatge o PDF)</li>
            <li>Passa el cursor per sobre i copia la URL</li>
            <li>Enganxa la URL on necessitis (minisites, pàgines, etc.)</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMedia;
